import { useEffect, useState } from "react";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

export default function EmailLogs() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = () => api.get("/email-logs").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllOnPage = () => setSelected((s) => {
    const allIds = rows.map((r) => r.log_id);
    const allSelected = allIds.every((id) => s.has(id));
    if (allSelected) { const n = new Set(s); allIds.forEach((id) => n.delete(id)); return n; }
    const n = new Set(s); allIds.forEach((id) => n.add(id)); return n;
  });
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post("/email-logs/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted} log entr${r.data.deleted === 1 ? "y" : "ies"}`);
      clearSelection();
      setBulkDeleteOpen(false);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkDeleting(false); }
  };

  return (
    <div>
      <PageHeader testid="page-email-logs" title="Email logs" subtitle={`${rows.length} events · audit-safe (identifiers redacted)`} />
      <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 border border-slate-200 rounded-md bg-white overflow-hidden">
          {selected.size > 0 && (
            <div data-testid="logs-bulk-bar" className="bg-slate-900 text-slate-100 px-4 py-2 flex items-center justify-between text-sm">
              <span data-testid="logs-selected-count">
                <span className="font-semibold">{selected.size}</span> selected
              </span>
              <div className="flex items-center gap-3">
                <button type="button" onClick={clearSelection} className="text-slate-300 hover:text-white text-xs" data-testid="logs-clear-selection">Clear</button>
                <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)} className="h-8" data-testid="logs-bulk-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete selected
                </Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
              <tr>
                <th className="w-10 px-4 py-2">
                  <Checkbox
                    data-testid="logs-select-all"
                    checked={rows.length > 0 && rows.every((r) => selected.has(r.log_id))}
                    onCheckedChange={toggleAllOnPage}
                    aria-label="Select all logs on this page"
                  />
                </th>
                <th className="text-left px-4 py-2">When</th>
                <th className="text-left px-4 py-2">To</th>
                <th className="text-left px-4 py-2">Subject</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">No emails logged yet.</td></tr>}
              {rows.map((r) => (
                <tr
                  key={r.log_id}
                  data-testid="log-row"
                  className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${selected.has(r.log_id) ? "bg-slate-50" : ""}`}
                >
                  <td className="px-4 py-3">
                    <Checkbox
                      data-testid={`log-row-select-${r.log_id}`}
                      checked={selected.has(r.log_id)}
                      onCheckedChange={() => toggleOne(r.log_id)}
                      aria-label={`Select log ${r.subject}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs cursor-pointer" onClick={() => setDetail(r)}>{fmtDateTime(r.sent_at)}</td>
                  <td className="px-4 py-3 text-slate-700 cursor-pointer" onClick={() => setDetail(r)}>{r.to_email}{r.redirected_to_admin && <span className="ml-1 text-[11px] text-amber-700">(redirected)</span>}</td>
                  <td className="px-4 py-3 text-slate-700 truncate max-w-xs cursor-pointer" onClick={() => setDetail(r)}>{r.subject}</td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => setDetail(r)}><StatusBadge status={r.status} label={r.status.replace("_", " ")} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-2 border border-slate-200 rounded-md bg-white p-5">
          {!detail ? <div className="text-sm text-slate-500 text-center py-12">Pick a row to preview.</div> : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="text-slate-500">Sent</div><div className="col-span-2 font-mono">{fmtDateTime(detail.sent_at)}</div>
                <div className="text-slate-500">To</div><div className="col-span-2">{detail.to_email}</div>
                {detail.original_to && (<><div className="text-slate-500">Original recipient</div><div className="col-span-2 text-amber-700">{detail.original_to}</div></>)}
                <div className="text-slate-500">Status</div><div className="col-span-2"><StatusBadge status={detail.status} /></div>
              </div>
              <div>
                <div className="text-slate-500 text-xs mb-1">Subject</div>
                <div className="font-medium text-slate-900">{detail.subject}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs mb-1">Body</div>
                <div className="border border-slate-200 rounded-md p-3 bg-slate-50 whitespace-pre-wrap font-mono text-[13px] text-slate-800 leading-relaxed">
                  {detail.body}
                </div>
              </div>
              {detail.note && <div className="text-xs text-slate-500">{detail.note}</div>}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="logs-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} log entr{selected.size === 1 ? "y" : "ies"}?</AlertDialogTitle>
            <AlertDialogDescription>
              These are audit records of past send attempts. Deleting them will remove your history
              of what was sent (and when). The actual chasers/reminders they were generated from are
              unaffected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="logs-bulk-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {bulkDeleting ? "Deleting…" : `Yes, delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
